// Home dashboard — post-agent redesign, the home page IS the agent.
//
// The previous surface (welcome header + onboarding status panel + 3
// action cards + token debug) has been rehomed:
//   • Brand voice + identity  → /brand
//   • Connected integrations  → /integrations (new page)
//   • Campaigns entry point   → /campaigns (secondary nav)
//   • Ad generation entry     → /generate-ads (via Campaigns / wizard)
//   • Onboarding progress     → /brand (OnboardingStatusPanel still lives there)
// Result: the chat is the ONLY thing on /home. Full-height fold.

import { useEffect, useState } from 'react';
import { Box, VStack } from '@chakra-ui/react';
import { useBrand } from '../../brand/BrandContext';
import { AgentChat, useSetAgentContext } from '../../agent';
import { listMarketingPlans } from '../../marketingPlans/client';
import { planOnboardingEnabled } from '../../marketingPlans/onboarding';
import { PlanOnboardingResume } from '../Onboarding/PlanOnboardingResume';

// `viewport` is the standalone /home fold: the chat itself is the page,
// so its box is calc(100vh - shell padding). `fill` is for a parent
// that already owns that viewport budget (Home v2). A second 100vh
// box under the v2 header would overflow the screen.
export function HomePage({ frame = 'viewport' }: { frame?: 'viewport' | 'fill' } = {}) {
  const { activeBrand } = useBrand();
  const currentPlanId = useListedCurrentPlanId(activeBrand?.id ?? null);
  // Same currentPlanId the Brand plans list exposes. Chat drafts against
  // that revision. An older acceptedPlanId is not sent.
  useSetAgentContext(currentPlanId ? { planId: currentPlanId } : {});
  const chat = (
    <AgentChat
      brandName={activeBrand?.name}
      brandId={activeBrand?.id}
      fill
    />
  );
  // Container height compensates for PipelineShell's vertical padding
  // (py 6/10 on mobile/desktop) so the chat card fills the visible
  // viewport without introducing an outer scrollbar. The resume banner
  // is the only reason to wrap the chat, and only while onboarding is on.
  const frameProps = frame === 'fill'
    ? { h: '100%', minH: 0, 'data-home-frame': 'fill' as const }
    : {
        h: { base: 'calc(100vh - 8rem)', md: 'calc(100vh - 10rem)' },
        'data-home-frame': 'viewport' as const,
      };
  if (!planOnboardingEnabled()) {
    return (
      <Box {...frameProps} data-testid="home-chat">
        {chat}
      </Box>
    );
  }
  return (
    <VStack align="stretch" spacing={3} {...frameProps} data-testid="home-chat-with-resume">
      <PlanOnboardingResume brandId={activeBrand?.id ?? null} />
      <Box flex="1" minH={0}>
        {chat}
      </Box>
    </VStack>
  );
}

function useListedCurrentPlanId(brandId: string | null): string | null {
  const [planId, setPlanId] = useState<string | null>(null);
  useEffect(() => {
    if (!brandId) {
      setPlanId(null);
      return;
    }
    let cancelled = false;
    setPlanId(null);
    void listMarketingPlans(brandId).then((result) => {
      if (!cancelled) setPlanId(result.currentPlanId);
    });
    return () => { cancelled = true; };
  }, [brandId]);
  return planId;
}

export default HomePage;
